import React from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Grid, Icon } from "semantic-ui-react";

import { DetailsCard } from "components";
import { TYRE_LOCATIONS } from "models";
import { CustomerCommunicationTyreTableItems } from "modules/AppointmentDetails/components/CustomerCommunicationModal/components/CustomerCommunicationReplacements/components";
import "modules/AppointmentDetails/components/CustomerCommunicationModal/components/CustomerCommunicationReplacements/CustomerCommunicationReplacements.scss";
import { getAxleName, getSpareIndex, sortAndGroupByAxle } from "modules/AppointmentDetails/components/VehicleTyres/utils";

import type { Appointment, VehicleTyre } from "models";
import type { SemanticWIDTHS } from "semantic-ui-react";

type CustomerCommunicationReplacementsProps = {
  appointment: Appointment;
  showVat: boolean;
  invalidContent?: boolean;
  onSwitchShowVat: () => void;
};

const TyreHeaders = () => {
  const { t } = useTranslation();

  const tyreColumns: { header: string; width: SemanticWIDTHS }[] = [
    { header: t("v8_position", "Position"), width: 2 },
    { header: t("v8_size", "Size"), width: 2 },
    { header: t("v8_type", "Type"), width: 3 },
    { header: t("v8_brand", "Brand"), width: 2 },
    { header: t("v8_profile", "Profile"), width: 1 },
    { header: t("v8_remarks", "Remarks"), width: 3 },
    { header: t("v8_price", "Price"), width: 1 },
    { header: "", width: 2 }
  ];

  return (
    <Grid>
      <Grid.Row className="header">
        {tyreColumns.map(item => (
          <Grid.Column key={item.header} width={item.width}>
            <p>{item.header}</p>
          </Grid.Column>
        ))}
      </Grid.Row>
    </Grid>
  );
};

export const CustomerCommunicationReplacements = ({ appointment, showVat, onSwitchShowVat, invalidContent }: CustomerCommunicationReplacementsProps) => {
  const { t } = useTranslation();

  if (!appointment.car?.tyres?.some(t => t.location === TYRE_LOCATIONS.OnVehicle && t.replacements?.length)) return null;

  const vehicleTyres: VehicleTyre[] = [];
  let replacementsLength = 0;
  let visibleReplacementsLength = 0;
  appointment.car.tyres.forEach(tyre => {
    if (tyre.location !== TYRE_LOCATIONS.OnVehicle) return;

    tyre.replacements?.forEach(r => {
      replacementsLength++;
      if (r.visible_to_customer) visibleReplacementsLength++;
    });

    vehicleTyres.push(tyre);
  });

  const tyresByAxle = sortAndGroupByAxle(vehicleTyres);
  const truckSpareIndex = getSpareIndex(tyresByAxle);

  return (
    <DetailsCard
      title={`${t("v8_tyre_replacements", "Tyre Replacements")} ${replacementsLength ? `(${replacementsLength})` : ""}`}
      icon="tire regular"
      isExpanded={true}
      showExpandArrow={false}
      containerStyle={invalidContent && !visibleReplacementsLength ? "error" : ""}
      rightContent={
        <div className="full end">
          <p>{t("v8_show_prices_with_vat", "Show prices with VAT")}</p>
          <Checkbox toggle checked={showVat} onChange={onSwitchShowVat} />
        </div>
      }
    >
      <div className="CustomerCommunicationReplacements">
        <div className="CustomerCommunicationReplacements-container">
          {vehicleTyres.length > 0 && (
            <div className="CustomerCommunicationReplacements-section">
              <div className="CustomerCommunicationReplacements-section-group">
                <div className="CustomerCommunicationReplacements-section-group_header">
                  <Icon className="car mirrors" />
                  <p>{t("v8_on_car", "On Car").toUpperCase()}</p>
                </div>
                <TyreHeaders />
                {Array.from(tyresByAxle, ([axle, tyres]) => (
                  <React.Fragment key={"axle-" + axle}>
                    {axle > 0 && tyres.length > 0 && (
                      <div className="axle-header">
                        <p>{getAxleName(tyres[0], truckSpareIndex, t)}</p>
                      </div>
                    )}
                    {tyres.map((tyre, idx) => (
                      <CustomerCommunicationTyreTableItems
                        key={tyre.id}
                        vehicleTyre={tyre}
                        showVat={showVat}
                        appointment_status_identifier={appointment.appointment_status_identifier}
                        isLastInGroup={idx === vehicleTyres.length - 1}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </DetailsCard>
  );
};
